import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Phone, MessageSquare, MapPin, Languages, HeartPulse, ExternalLink, Download, Smartphone, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { cacheEmergency, readEmergency } from "@/lib/offlineCache";

function Row({ label, value }) {
  return (
    <div className="py-2 border-b border-white/15 last:border-0">
      <p className="text-sm uppercase tracking-wide text-white/60">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

export default function Emergency() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offlineAt, setOfflineAt] = useState(null);
  const [showLocal, setShowLocal] = useState(false);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api
      .get("/emergency")
      .then(({ data }) => {
        setData(data);
        setOfflineAt(null);
        cacheEmergency(data);
      })
      .catch(() => {
        const c = readEmergency();
        if (c) {
          setData(c.data);
          setOfflineAt(c.savedAt);
        }
      });
  }, []);

  if (!data) {
    return (
      <AppShell active="emergency">
        <div className="h-40 bg-[#1A1A1A] rounded-2xl border border-[#333333] animate-pulse" />
      </AppShell>
    );
  }

  const ms = data.medical_summary;
  const sl = data.translations.summary_local;
  const useLocal = showLocal && !!sl;
  const labels = showLocal ? data.translations.labels : data.translations.english_labels;
  const none = labels.none_recorded;
  const list = (arr) => (arr && arr.length ? arr.join(", ") : none);

  const conditions = useLocal ? sl.conditions : ms.conditions;
  const medications = useLocal ? sl.medications : ms.medications;
  const allergies = useLocal ? sl.allergies : ms.allergies;
  const commNeeds = useLocal ? sl.communication_needs : ms.communication_needs;

  const smsBody = encodeURIComponent(
    `I need help. Please check on me. My location: ${data.maps_link || ""}`
  );
  const smsHref = ms.emergency_contact_phone
    ? `sms:${ms.emergency_contact_phone.replace(/\s/g, "")}?body=${smsBody}`
    : null;

  const saveCard = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#111111",
        filter: (node) => !(node?.dataset && node.dataset.noCapture === "true"),
      });
      const link = document.createElement("a");
      link.download = `pocketdoc-medical-summary${showLocal ? "-" + data.language.toLowerCase() : ""}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Medical card saved to your device.");
    } catch (e) {
      console.error("save medical card failed", e);
      toast.error("Could not save the card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell active="emergency">
      <div className="flex items-center gap-2 mb-2">
        <HeartPulse className="text-[#D32F2F]" />
        <h1 className="font-head text-3xl font-extrabold text-[#F8FAFC]">Emergency</h1>
      </div>
      {data.active_trip ? (
        <p className="text-[#A1A1AA]">
          Set for {data.active_trip.city}, {data.active_trip.country}
        </p>
      ) : (
        <p className="text-[#A1A1AA]">No active trip — showing default emergency number.</p>
      )}

      {offlineAt && (
        <div data-testid="emergency-offline-banner" className="mt-3 flex items-center gap-2 text-xs text-[#EFC77A] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl px-3 py-2">
          <WifiOff size={14} className="shrink-0" />
          <span>Offline — showing your saved copy from {new Date(offlineAt).toLocaleDateString()}.</span>
        </div>
      )}

      {/* Call button — largest element */}
      <a
        href={`tel:${data.emergency_number}`}
        data-testid="call-emergency-btn"
        aria-label={`Call local emergency services on ${data.emergency_number}`}
        className="mt-5 w-full min-h-[110px] rounded-2xl bg-[#D32F2F] text-white flex flex-col items-center justify-center shadow-[0_8px_24px_rgba(211,47,47,0.35)] active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-3">
          <Phone size={30} />
          <span className="font-head text-2xl font-extrabold">Call {data.emergency_number}</span>
        </div>
        <span className="text-white/90 text-sm mt-1">Call local emergency services</span>
      </a>
      <p className="mt-2 text-sm text-[#A1A1AA] text-center" data-testid="emergency-disclaimer">
        PocketDocta is not an emergency service. This calls your local emergency number directly.
      </p>

      {/* Medical summary (captured as image) */}
      <div className="mt-6 bg-[#1A1A1A] text-white rounded-2xl p-5" data-testid="medical-summary-card" ref={cardRef}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-head text-xl font-bold">{labels.title}</h2>
          <button
            data-testid="translate-btn"
            data-no-capture="true"
            onClick={() => setShowLocal((s) => !s)}
            className="inline-flex items-center gap-1 text-sm font-semibold bg-white/10 rounded-full px-3 py-2 min-h-[44px]"
          >
            <Languages size={16} />
            {showLocal ? "Show in English" : `Show in ${data.language}`}
          </button>
        </div>
        <Row label={labels.conditions} value={list(conditions)} />
        <Row label={labels.medications} value={list(medications)} />
        <Row label={labels.allergies} value={list(allergies)} />
        <Row
          label={labels.emergency_contact}
          value={
            ms.emergency_contact_name
              ? `${ms.emergency_contact_name} — ${ms.emergency_contact_phone}`
              : none
          }
        />
        {commNeeds && (
          <p className="mt-3 text-white/90 text-sm bg-white/10 rounded-xl p-3">{commNeeds}</p>
        )}
        <p className="mt-3 text-[11px] text-white/50">
          PocketDocta · Not medical advice · Emergency: {data.emergency_number}
        </p>
      </div>

      {/* Translation status + Save */}
      {showLocal && !data.translations.translated_live && (
        <p className="mt-2 text-xs text-[#A1A1AA]" data-testid="translation-status">
          Labels are translated; enable Google Cloud Translation to translate your condition and
          medication text too.
        </p>
      )}
      <button
        data-testid="save-card-btn"
        onClick={saveCard}
        disabled={saving}
        className="mt-3 w-full min-h-[56px] rounded-xl bg-[#1A1A1A] border-2 border-[#1A1A1A] text-[#F8FAFC] flex items-center justify-center gap-2 font-semibold"
      >
        <Download size={20} /> {saving ? "Saving…" : "Save medical card (image)"}
      </button>
      <button
        data-testid="open-medcard-btn"
        onClick={() => navigate("/medical-card")}
        className="mt-3 w-full min-h-[52px] rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center gap-2 font-semibold"
      >
        <Smartphone size={20} /> Open full-screen Medical ID
      </button>

      {/* Nearest facility */}
      {(data.nearest_appropriate_facility || data.maps_link) && (
        <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4" data-testid="nearest-facility-card">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={18} className="text-[#EF9F27]" />
            <h3 className="font-head font-bold text-[#F8FAFC]">Nearest appropriate facility</h3>
          </div>
          <p className="text-[#F8FAFC] text-sm">
            {data.nearest_appropriate_facility || "Generate a trip report to see the best-matched facility."}
          </p>
          {data.maps_link && (
            <a
              href={data.maps_link}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#EF9F27]"
            >
              Find hospitals in Maps <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}

      {/* Notify contact */}
      {smsHref ? (
        <a
          href={smsHref}
          data-testid="notify-contact-btn"
          className="mt-4 w-full min-h-[64px] rounded-2xl bg-[#1A1A1A] border-2 border-[#1A1A1A] text-[#F8FAFC] flex items-center justify-center gap-2 font-semibold text-lg"
        >
          <MessageSquare size={22} /> Notify {ms.emergency_contact_name || "my contact"}
        </a>
      ) : (
        <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 text-sm text-[#A1A1AA]">
          Add an emergency contact in your profile to enable one-tap SMS alerts.
        </div>
      )}
    </AppShell>
  );
}
